/**
 * Frontend Web Speech API wrapper for the voice toolbar button
 * (VOY-474). Browser-native — no backend.
 *
 * Tested in Chrome (vendor-prefixed `webkitSpeechRecognition`) and
 * Safari (currently no native support — `isSupported()` returns false
 * so the caller can disable the mic UI). Firefox: also unsupported in
 * most current versions.
 */

interface SpeechRecognitionLike {
  continuous: boolean
  interimResults: boolean
  lang: string
  start: () => void
  stop: () => void
  abort: () => void
  onresult: ((event: SpeechRecognitionResultEventLike) => void) | null
  onerror: ((event: { error: string; message?: string }) => void) | null
  onend: (() => void) | null
  onstart: (() => void) | null
}

interface SpeechRecognitionResultEventLike {
  results: ArrayLike<ArrayLike<{ transcript: string; confidence: number }> & { isFinal: boolean }>
  resultIndex: number
}

interface SpeechRecognitionConstructor {
  new (): SpeechRecognitionLike
}

function getSpeechRecognitionCtor(): SpeechRecognitionConstructor | undefined {
  if (typeof window === 'undefined') return undefined
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionConstructor
    webkitSpeechRecognition?: SpeechRecognitionConstructor
  }
  return w.SpeechRecognition ?? w.webkitSpeechRecognition
}

/**
 * Whether the current browser exposes the Web Speech API at all.
 * SmartSearchCard uses this to decide whether to render the mic button
 * as enabled.
 */
export function isVoiceTranscribeSupported(): boolean {
  return getSpeechRecognitionCtor() !== undefined
}

export interface VoiceSession {
  /** Stop the active recognition. Idempotent. */
  stop: () => void
}

export interface VoiceTranscribeHandlers {
  /** Fires on every result update (interim + final). The full
   *  concatenated transcript so far is passed — caller can replace
   *  `state.query` verbatim. */
  onTranscript: (transcript: string, isFinal: boolean) => void
  /** Fires once the engine ends (silence detected or `stop()` called).
   *  Receives the final transcript. */
  onEnd?: (finalTranscript: string) => void
  /** Fires on browser-level errors. */
  onError?: (message: string) => void
}

/**
 * Start a voice recognition session. Returns a handle whose `stop()`
 * cancels the active session. Throws synchronously if the browser
 * doesn't support the API — callers should gate via
 * `isVoiceTranscribeSupported()` first.
 */
export function startVoiceTranscribe(handlers: VoiceTranscribeHandlers): VoiceSession {
  const Ctor = getSpeechRecognitionCtor()
  if (!Ctor) {
    throw new Error('Voice input is not supported in this browser')
  }

  const recognition = new Ctor()
  recognition.continuous = true
  recognition.interimResults = true
  // Hardcoded for VOY-474. Voy's travel app is international, so a
  // future i18n ticket should read this from user locale / profile
  // language preference rather than ship monolingual.
  recognition.lang = 'en-US'

  let lastTranscript = ''
  let stopped = false

  recognition.onresult = (event) => {
    let combined = ''
    for (let i = 0; i < event.results.length; i++) {
      const result = event.results[i]
      const alternative = result[0]
      if (alternative?.transcript) {
        combined += alternative.transcript
      }
    }
    // In continuous mode, once a segment is marked `isFinal` it never
    // flips back — so checking "did any segment ever go final" would
    // latch `true` for every subsequent onresult, including purely
    // interim ones appended after the first utterance settled.
    // The right signal is whether the LATEST segment is final.
    // (PR #277 review fix #2.)
    const last = event.results[event.results.length - 1]
    const lastIsFinal = !!last?.isFinal
    lastTranscript = combined.trim()
    handlers.onTranscript(lastTranscript, lastIsFinal)
  }

  recognition.onerror = (event) => {
    handlers.onError?.(event.message ?? event.error ?? 'Voice recognition error')
  }

  recognition.onend = () => {
    if (stopped) return
    stopped = true
    handlers.onEnd?.(lastTranscript)
  }

  try {
    recognition.start()
  } catch (err) {
    // Already-started or hardware issue; surface via onError.
    handlers.onError?.(err instanceof Error ? err.message : String(err))
  }

  return {
    stop: () => {
      if (stopped) return
      stopped = true
      try {
        recognition.stop()
      } catch {
        // ignore — recognition may already be stopped
      }
      handlers.onEnd?.(lastTranscript)
    },
  }
}
